import { calculatePlanContributionTaxes } from '@Common/bo/calculate_plan_contribution_taxes';
import { B2bSavingPlanDeductibility } from '@Common/types/b2b/plans';
import {
  GetB2bCampaignAllocationOneResponse,
  ValueSharingBonusFiscalRule,
} from '@Common/types/bo/b2b/campaigns';
import { TaxResidenceCode } from '@Common/types/bo/b2b/employees';
import { makeIntegerAmount } from '@Common/types/currency/currency';

export type CampaignAllocationChoice = {
  id: number;
  campaign_id: number;
  company_id: number;
  employee_id: number;
  identifier: string | null;
  firstname: string | null;
  lastname: string | null;
  registration_number: string | null;
  social_status: string | null;
  tax_residence_code: string | null;
  net_amount: number | null;
  gross_amount: number | null;
  plan_id: number | null;
  plan_type: string | null;
  plan_label: string | null;
  allocation_type: string | null;
  instrument_id: number | null;
  risk_profile: string | null;
  deductibility: B2bSavingPlanDeductibility | null;
  instrument_name: string | null;
  csg_amount: number | null;
  crds_amount: number | null;
  late_interests_amount: number;
  matching_contribution_gross_amount: number;
  matching_contribution_net_amount: number;
};

function calculateMatchingContributionNetAmount(
  grossAmount: number,
  socialStatus: string | null,
  taxResidenceCode: TaxResidenceCode | null,
  valueSharingBonusFiscalRule: ValueSharingBonusFiscalRule | null,
): number {
  if (grossAmount > 0 && socialStatus && taxResidenceCode) {
    const matchingTaxDetails = calculatePlanContributionTaxes({
      socialStatus: socialStatus as 'TS' | 'TNS',
      amount: makeIntegerAmount(grossAmount),
      isNetAmountTaxContribution: false,
      taxResidenceCode,
      valueSharingBonusFiscalRule,
    });
    return matchingTaxDetails.userCurrencyAmount;
  }
  return 0;
}

export function flattenCampaignAllocationsPerChoice(
  groupedAllocations: GetB2bCampaignAllocationOneResponse[],
  valueSharingBonusFiscalRule: ValueSharingBonusFiscalRule | null,
  hasLateInterestsInGrossAmount: boolean,
): CampaignAllocationChoice[] {
  return groupedAllocations.flatMap((allocation) => {
    const choices = allocation.company_collected_choices;
    const validChoices = choices.filter(
      (choice) => choice.gross_amount !== null && choice.amount !== null,
    );

    if (validChoices.length === 0) {
      return [
        {
          id: allocation.id,
          campaign_id: allocation.campaign_id,
          company_id: allocation.company_id,
          employee_id: allocation.employee_id,
          identifier: allocation.identifier,
          firstname: allocation.firstname,
          lastname: allocation.lastname,
          registration_number: allocation.registration_number,
          social_status: allocation.social_status,
          tax_residence_code: allocation.tax_residence_code,
          net_amount: allocation.net_amount,
          gross_amount: allocation.gross_amount,
          plan_id: null,
          plan_type: null,
          plan_label: null,
          allocation_type: null,
          instrument_id: null,
          risk_profile: null,
          instrument_name: null,
          deductibility: null,
          csg_amount: allocation.csg_amount ?? 0,
          crds_amount: allocation.crds_amount ?? 0,
          late_interests_amount: allocation.late_interests_amount,
          matching_contribution_gross_amount: allocation.matching_contribution_amount,
          matching_contribution_net_amount: calculateMatchingContributionNetAmount(
            allocation.matching_contribution_amount,
            allocation.social_status,
            allocation.tax_residence_code,
            valueSharingBonusFiscalRule,
          ),
        },
      ];
    }

    return validChoices.map((choice) => {
      const totalGross =
        (allocation.gross_amount ?? 0) -
        (hasLateInterestsInGrossAmount ? allocation.late_interests_amount : 0);
      const choiceGross = choice.gross_amount ?? 0;
      const proportion = totalGross > 0 ? choiceGross / totalGross : 0;

      const choiceCsgAmount = choice.csg_amount ?? 0;
      const choiceCrdsAmount = choice.crds_amount ?? 0;
      const choiceLateInterestsAmount = Math.round(allocation.late_interests_amount * proportion);
      const choiceMatchingGrossAmount = choice.matching_contribution_gross_amount ?? 0;
      const choiceMatchingNetAmount = choice.matching_contribution_amount ?? 0;

      return {
        id: allocation.id,
        campaign_id: allocation.campaign_id,
        company_id: allocation.company_id,
        employee_id: allocation.employee_id,
        identifier: allocation.identifier,
        firstname: allocation.firstname,
        lastname: allocation.lastname,
        registration_number: allocation.registration_number,
        social_status: allocation.social_status,
        tax_residence_code: allocation.tax_residence_code,
        net_amount: choice.amount,
        gross_amount: choice.gross_amount,
        plan_id: choice.plan_id,
        plan_type: choice.plan_type,
        plan_label: choice.plan_label,
        allocation_type: choice.allocation_type,
        instrument_id: choice.instrument_id,
        risk_profile: choice.risk_profile,
        instrument_name: choice.instrument_name,
        deductibility: choice.deductibility,
        csg_amount: choiceCsgAmount,
        crds_amount: choiceCrdsAmount,
        late_interests_amount: choiceLateInterestsAmount,
        matching_contribution_gross_amount: choiceMatchingGrossAmount,
        matching_contribution_net_amount: choiceMatchingNetAmount,
      };
    });
  });
}
